import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'

import UploadsTab from './UploadsTab'
import RegistryTab from './RegistryTab'

/**
 * `/settings/knowledge-graph` — internal eng-ops surface.
 *
 * Two tabs:
 *   - **Uploads** (AGNT-698) — per-upload pipeline state.
 *     Existing 908-LOC content, extracted to `UploadsTab.tsx` so the
 *     tab router stays thin and the original tests target the same
 *     component body.
 *   - **Registry** (Polish Gap #18b) — per-tenant Entity Registry +
 *     R6 proposal queue, sourced from the `/v1/kg-admin/*` endpoints
 *     shipped in Gap #18a.
 *
 * Tab choice lives in component state — no URL param — because the
 * default landing (`Uploads`) is unchanged and the existing
 * SettingsPage routing only knows about the `knowledge-graph`
 * section, not sub-tabs. Future deep-link support is a follow-up.
 *
 * Accessibility (greptile #12065): the tablist is a plain `<div
 * role="tablist">`, not a `<nav>`, so we don't clobber the native
 * navigation landmark. Each `<button role="tab">` carries
 * `aria-controls={panelId}`, and the body `<div role="tabpanel">`
 * carries `id={panelId}` + `aria-labelledby={tabId}` so a screen
 * reader can navigate from the tab to the panel it owns.
 */
type Tab = 'uploads' | 'registry'

const TAB_IDS: Record<Tab, { tabId: string; panelId: string }> = {
  uploads: { tabId: 'kg-tab-uploads', panelId: 'kg-panel-uploads' },
  registry: { tabId: 'kg-tab-registry', panelId: 'kg-panel-registry' },
}

export default function KnowledgeGraph() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('uploads')
  const { tabId, panelId } = TAB_IDS[tab]
  return (
    <div className="flex h-full flex-col">
      <div
        role="tablist"
        aria-label={t('common:knowledge_graph.tabs.aria', 'Knowledge graph sections')}
        className="flex shrink-0 gap-1 border-b border-gray-200 px-4 pt-2 dark:border-dark-border"
      >
        <TabButton
          tabId={TAB_IDS.uploads.tabId}
          panelId={TAB_IDS.uploads.panelId}
          active={tab === 'uploads'}
          onClick={() => setTab('uploads')}
          label={t('common:knowledge_graph.tabs.uploads', 'Uploads')}
        />
        <TabButton
          tabId={TAB_IDS.registry.tabId}
          panelId={TAB_IDS.registry.panelId}
          active={tab === 'registry'}
          onClick={() => setTab('registry')}
          label={t('common:knowledge_graph.tabs.registry', 'Registry')}
        />
      </div>
      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId}
        className="flex-1 overflow-auto"
      >
        {tab === 'uploads' ? <UploadsTab /> : <RegistryTab />}
      </div>
    </div>
  )
}

interface TabButtonProps {
  tabId: string
  panelId: string
  active: boolean
  onClick: () => void
  label: string
}

function TabButton({ tabId, panelId, active, onClick, label }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-controls={panelId}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={cn(
        'rounded-t border-b-2 px-3 py-2 text-sm',
        active
          ? 'border-blue-600 font-medium text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-dark-text-muted dark:hover:text-dark-text',
      )}
    >
      {label}
    </button>
  )
}
